import { NextRequest, NextResponse } from 'next/server'
import { and, desc, eq } from 'drizzle-orm'
import { createTransport } from 'nodemailer'
import { db } from '@/db'
import { aiArtifacts, auditLog, tenantConfigs, ticketMessages, tickets, users } from '@/db/schema'
import { requireExpertApiUser } from '@/lib/role-guard'
import { transition } from '@/lib/ticket-state-machine'
import { inngest } from '@/lib/inngest'
import { buildResponseNotificationEmail } from '@/lib/response-notification-email'

function sanitize(text: string): string {
  return text.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
}

export async function POST(
  request: NextRequest,
  { params }: { params: Promise<{ ticketId: string }> },
) {
  const guard = await requireExpertApiUser()
  if (!guard.ok) return guard.response

  const { ticketId } = await params
  const body = await request.json().catch(() => null)
  if (!body?.responseText || typeof body.responseText !== 'string') {
    return NextResponse.json({ error: 'responseText is required' }, { status: 400 })
  }
  if (!body?.resolutionSeverity || typeof body.resolutionSeverity !== 'string') {
    return NextResponse.json({ error: 'resolutionSeverity is required' }, { status: 400 })
  }
  if (!body?.resolutionConcerns || typeof body.resolutionConcerns !== 'string') {
    return NextResponse.json({ error: 'resolutionConcerns is required' }, { status: 400 })
  }

  const [ticket] = await db
    .select()
    .from(tickets)
    .where(and(eq(tickets.id, ticketId), eq(tickets.tenantId, guard.user.tenantId)))
    .limit(1)

  if (!ticket) return NextResponse.json({ error: 'Ticket not found' }, { status: 404 })
  if (ticket.status !== 'RESPONSE_READY') {
    return NextResponse.json(
      { error: `Cannot respond while ticket is ${ticket.status}` },
      { status: 409 },
    )
  }

  const responseText = body.responseText.trim()
  if (responseText.length < 10) {
    return NextResponse.json({ error: 'responseText must be at least 10 characters' }, { status: 422 })
  }

  const [latestDraft] = await db
    .select()
    .from(aiArtifacts)
    .where(and(eq(aiArtifacts.ticketId, ticket.id), eq(aiArtifacts.artifactType, 'suggested_response')))
    .orderBy(desc(aiArtifacts.createdAt))
    .limit(1)

  const [message] = await db
    .insert(ticketMessages)
    .values({
      tenantId: guard.user.tenantId,
      ticketId: ticket.id,
      authorId: guard.user.id,
      role: 'expert',
      bodyRaw: responseText,
      bodySanitized: sanitize(responseText),
    })
    .returning()

  const resolutionSummary = typeof body.resolutionSummary === 'string' && body.resolutionSummary.trim()
    ? body.resolutionSummary.trim()
    : null

  const nextStatus = transition(ticket.status, 'RESPONDED')
  const [updated] = await db
    .update(tickets)
    .set({
      status: nextStatus,
      assignedExpertId: guard.user.id,
      lastMessageId: message.id,
      resolutionSeverity: body.resolutionSeverity,
      resolutionConcerns: body.resolutionConcerns,
      resolutionSummary,
      version: ticket.version + 1,
      updatedAt: new Date(),
    })
    .where(and(eq(tickets.id, ticket.id), eq(tickets.version, ticket.version)))
    .returning()

  if (!updated) return NextResponse.json({ error: 'Ticket was updated, retry response' }, { status: 409 })

  if (latestDraft) {
    await db
      .update(aiArtifacts)
      .set({
        expertAccepted: latestDraft.content.trim() === responseText,
        expertEdited: latestDraft.content.trim() !== responseText,
        expertVersion: responseText,
      })
      .where(eq(aiArtifacts.id, latestDraft.id))
  }

  await db.insert(auditLog).values({
    tenantId: guard.user.tenantId,
    actorId: guard.user.id,
    actorType: 'user',
    action: 'ticket.responded',
    entityType: 'ticket',
    entityId: ticket.id,
    payload: { from: ticket.status, to: nextStatus, draftArtifactId: latestDraft?.id ?? null },
  })

  try {
    await inngest.send({
      name: 'ticket/responded',
      data: {
        ticketId: ticket.id,
        tenantId: guard.user.tenantId,
        expertId: guard.user.id,
        responseMessageId: message.id,
      },
    })
  } catch {
    // Inngest unavailable — response recorded; KB proposal will not fire until re-triggered
  }

  // Email the line manager — fire-and-forget, never fail the response if email errors
  try {
    const [submitter] = await db
      .select({ email: users.email })
      .from(users)
      .where(eq(users.id, ticket.submitterId))
      .limit(1)

    if (submitter?.email && process.env.EMAIL_SERVER_HOST) {
      const [cfg] = await db
        .select({ includeResponseInEmail: tenantConfigs.includeResponseInEmail })
        .from(tenantConfigs)
        .where(eq(tenantConfigs.tenantId, ticket.tenantId))
        .limit(1)
      const includeContent = cfg?.includeResponseInEmail ?? true
      const transport = createTransport({
        host: process.env.EMAIL_SERVER_HOST,
        port: Number(process.env.EMAIL_SERVER_PORT ?? 587),
        auth: process.env.EMAIL_SERVER_USER
          ? { user: process.env.EMAIL_SERVER_USER, pass: process.env.EMAIL_SERVER_PASSWORD }
          : undefined,
      })
      const { subject, html, text } = buildResponseNotificationEmail({
        ticketReference: ticket.referenceNumber ?? ticketId,
        ticketTitle: ticket.title ?? 'Your ticket',
        responseText,
        ticketId: ticket.id,
        includeContent,
      })
      await transport.sendMail({
        to: submitter.email,
        from: process.env.EMAIL_FROM,
        subject,
        html,
        text,
      })
    }
  } catch (err) {
    console.error('[respond] Failed to send notification email:', err)
    await db.insert(auditLog).values({
      tenantId: guard.user.tenantId,
      actorId: guard.user.id,
      actorType: 'user',
      action: 'ticket.notification_email_failed',
      entityType: 'ticket',
      entityId: ticket.id,
      payload: { error: String(err) },
    })
  }

  return NextResponse.json({ ticketId: ticket.id, messageId: message.id, status: updated.status })
}
